import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button, PublicContainer } from '@mts/ui';
import TrackableListingCard from '../common/TrackableListingCard';
import { useListings } from '../../hooks/useListings';
import { usePublicTenant } from '../../hooks/usePublicTenant';
import { compareListingsByTierPriority, getTierKey } from '../../utils/listingFeatures';
import { buildListingPath } from '../../utils/listingUrls';

const TIER_GRADIENTS = {
  basic: [
    'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600',
    'bg-gradient-to-br from-gray-800 via-zinc-700 to-stone-600',
    'bg-gradient-to-br from-slate-800 via-blue-800 to-sky-700',
  ],
  featured: [
    'bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900',
    'bg-gradient-to-br from-violet-900 via-purple-800 to-indigo-900',
    'bg-gradient-to-br from-zinc-900 via-gray-800 to-cyan-900',
  ],
  spotlight: [
    'bg-gradient-to-br from-stone-900 via-amber-900 to-orange-800',
    'bg-gradient-to-br from-neutral-900 via-yellow-900 to-amber-700',
    'bg-gradient-to-br from-slate-900 via-amber-900 to-red-900',
  ],
};

function getLogoText(name = '') {
  const words = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return 'LOCAL\nBUSINESS';
  }

  if (words.length === 1) {
    return words[0].toUpperCase();
  }

  return `${words[0].toUpperCase()}\n${words[1].toUpperCase()}`;
}

function mapListingToCard(listing, index) {
  const tier = getTierKey(listing);
  const tierKey = tier === 'spotlight' || tier === 'featured' ? tier : 'basic';
  const gradients = TIER_GRADIENTS[tierKey];

  return {
    id: listing.id || `${tierKey}-${index}`,
    slug: listing.slug,
    tier: tierKey,
    city: listing.city || '',
    listing,
    card: {
      name: listing.name || 'Local Business',
      category:
        listing.category ||
        listing.listingCategories?.[0]?.tenantCategory?.name ||
        'Local Business',
      rating: listing.rating ? String(listing.rating) : '',
      reviews: String(listing.reviewCount || listing.reviews || '0'),
      shortAddress:
        listing.shortAddress ||
        listing.shortAddressDescription ||
        listing.addressShort ||
        listing.addressLabel ||
        listing.details?.shortAddress ||
        listing.details?.shortAddressDescription ||
        listing.details?.addressShort ||
        listing.details?.addressLabel ||
        '',
      address:
        listing.address ||
        listing.details?.address ||
        listing.workspace?.basics?.address ||
        listing.ownerWorkspace?.basics?.address ||
        '',
      addressLine1:
        listing.addressLine1 ||
        listing.details?.addressLine1 ||
        listing.workspace?.basics?.addressLine1 ||
        listing.ownerWorkspace?.basics?.addressLine1 ||
        '',
      phone: listing.phone || listing.phoneNumber || listing.details?.phone || listing.details?.basics?.phone || '',
      website:
        listing.website ||
        listing.websiteUrl ||
        listing.url ||
        listing.details?.website ||
        listing.details?.websiteUrl ||
        listing.details?.basics?.website ||
        '',
      city: listing.city || '',
      description:
        listing.description ||
        'View business details and contact information.',
      imageGradient: gradients[index % gradients.length],
      logoBg: tierKey === 'spotlight' ? 'bg-stone-800' : 'bg-slate-800',
      logoText: getLogoText(listing.name),
    },
  };
}


function SpotlightCardSkeleton() {
  return (
    <div
      className="w-full shrink-0 sm:max-w-[350px] sm:basis-[350px]"
      aria-hidden="true"
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="h-44 animate-pulse bg-slate-200" />
        <div className="space-y-4 p-5">
          <div className="h-3 w-24 animate-pulse rounded-full bg-slate-200" />
          <div className="h-6 w-3/4 animate-pulse rounded bg-slate-200" />
          <div className="space-y-2">
            <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="h-10 w-full animate-pulse rounded-md bg-slate-200" />
        </div>
      </div>
    </div>
  );
}

function SpotlightCardsRow() {
  const navigate = useNavigate();
  const { listings, loading } = useListings();
  const { brandTheme } = usePublicTenant();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const spotlightCards = useMemo(() => {
    const availableListings = Array.isArray(listings) ? listings : [];
    const priorityListings = availableListings
      .filter((listing) => {
        const tier = getTierKey(listing);
        return tier === 'featured' || tier === 'spotlight';
      })
      .sort(compareListingsByTierPriority)
      .slice(0, 8);

    if (priorityListings.length > 0) {
      return priorityListings.map(mapListingToCard);
    }

    const basicListings = availableListings.filter((listing) => getTierKey(listing) === 'basic');
    const selectedBasicListings = basicListings.slice(0, 8);

    return selectedBasicListings.map(mapListingToCard);
  }, [listings]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return undefined;
    }

    const updateScrollButtons = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 4);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    updateScrollButtons();
    container.addEventListener('scroll', updateScrollButtons, { passive: true });
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [spotlightCards.length]);

  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: -320, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: 320, behavior: 'smooth' });
  };

  const handleViewProfile = (listing) => {
    const path = buildListingPath(listing?.listing || listing);
    if (!path) return;
    navigate(path);
  };

  return (
    <section className="pb-0">
      <style>{`.spotlight-card-row::-webkit-scrollbar{display:none;}`}</style>
      <PublicContainer>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-bold lg:text-3xl" style={{ color: 'var(--primary)' }}>In The Spotlight</h2>
          <Link
            to="/listings"
            className="text-sm font-semibold text-[var(--primary)] transition-colors hover:text-[var(--primary-hover)]"
          >
            View All
          </Link>
        </div>

        {!loading && !spotlightCards.length ? <p className="mt-5 text-text-secondary">No businesses published yet. Explore the directory as local businesses are added.</p> : null}
        <div className="mt-5 flex items-center gap-2">
          {canScrollLeft ? (
            <Button
              variant="secondary"
              aria-label="Scroll spotlight cards left"
              onClick={handleScrollLeft}
              className="hidden h-10 w-10 shrink-0 rounded-full border p-0 shadow-sm lg:inline-flex"
              style={{ borderColor: 'var(--ui-input-border)', color: 'var(--page-body-color)' }}
            >
              <ChevronLeft size={18} />
            </Button>
          ) : null}

          <div className="relative flex-1 min-w-0">
            {/* Mobile scroll fade hints — hidden on desktop */}
            {canScrollLeft && (
              <div
                className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-10 md:hidden"
                style={{ background: 'linear-gradient(to right, var(--background, #F8FAFC), transparent)' }}
              />
            )}
            {canScrollRight && (
              <div
                className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-16 md:hidden"
                style={{ background: 'linear-gradient(to left, var(--background, #F8FAFC), transparent)' }}
              />
            )}

            <div
              ref={scrollContainerRef}
              className="spotlight-card-row flex items-start gap-4 overflow-x-auto pt-0 pb-3"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              aria-busy={loading}
            >
              {loading
                ? Array.from({ length: 3 }, (_, index) => (
                    <SpotlightCardSkeleton key={`spotlight-skeleton-${index}`} />
                  ))
                : spotlightCards.map((item, index) => (
                  <TrackableListingCard
                    key={item.id}
                    listingId={item.id}
                    context="In The Spotlight"
                    section="In The Spotlight"
                    index={index}
                    variant="directory"
                    tier={item.tier}
                    listing={{ ...item.card, id: item.id, tier: item.tier }}
                    targetPath={buildListingPath(item.listing || item)}
                    onNavigate={() => handleViewProfile(item)}
                    show={{ address: false }}
                    className="h-full"
                    wrapperClassName="w-full shrink-0 sm:max-w-[350px] sm:basis-[350px]"
                  />
                  ))}
            </div>
          </div>

          {canScrollRight ? (
            <Button
              variant="secondary"
              aria-label="Scroll spotlight cards right"
              onClick={handleScrollRight}
              className="hidden h-10 w-10 shrink-0 rounded-full border p-0 shadow-sm lg:inline-flex"
              style={{ borderColor: 'var(--ui-input-border)', color: 'var(--page-body-color)' }}
            >
              <ChevronRight size={18} />
            </Button>
          ) : null}
        </div>
      </PublicContainer>
    </section>
  );
}

export default SpotlightCardsRow;
